import type { FunctionComponent } from 'react';
import React, { Suspense, useCallback, useState } from 'react';

import type { Board } from './PluginHeaderButton.types';
import { PluginListButton } from './PluginListButton';
import styles from './PluginHeaderHubButton.less';
import { useLazyComponent } from '@trello/use-lazy-component';
import { useNewFeature } from 'app/src/components/NewFeature';
import { useFeatureFlag } from '@trello/feature-flag-client';

interface PluginHeaderHubButtonProps {
  openDirectory: () => object;
  openEnabled: () => object;
  openPluginSettings: () => object;
  board: Board;
  container: Element;
}

export const PluginHeaderHubButton: FunctionComponent<PluginHeaderHubButtonProps> = ({
  openDirectory,
  openEnabled,
  openPluginSettings,
  board,
  container,
}: PluginHeaderHubButtonProps) => {
  const PluginListButtonSpotlight = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "plugin-list-button-spotlight" */ 'app/src/components/PluginHeaderButton'
      ),
    {
      namedImport: 'PluginListButtonSpotlight',
    },
  );
  const { acknowledgeNewFeature, isNewFeature } = useNewFeature('PupHeaderHub');
  // Because we don't want to acknowledge until the button has been
  // clicked we need to set a seperate value to show the spotlight initially
  // as if isNewFeature is used it causes a re-render on the soft
  // acknowledgeNewFeature call
  const [spotlightActive, setSpotlightActive] = useState(isNewFeature);
  const end = useCallback(() => {
    acknowledgeNewFeature({
      source: 'powerUpHubSettingsButton',
      explicit: true,
    });
    setSpotlightActive(false);
  }, [acknowledgeNewFeature]);
  const disableSpotlight = useFeatureFlag(
    'ecosystem.disable-pup-hub-spotlight',
    false,
  );

  return (
    <div className={styles.pluginContainer}>
      {spotlightActive && !disableSpotlight ? (
        <Suspense fallback={<></>}>
          <PluginListButtonSpotlight board={board} end={end} />
        </Suspense>
      ) : (
        <PluginListButton
          openDirectory={openDirectory}
          openEnabled={openEnabled}
          openPluginSettings={openPluginSettings}
          board={board}
          container={container}
        />
      )}
    </div>
  );
};
